import { create } from 'zustand';
import type { AssetRecord, CollageDocument, FontRecord, PhotoElement, TextElement } from '../../shared/document';
import { createDocument } from '../../shared/documentFactory';
import { validateDocument } from '../../shared/schemas';
import { serializeDocument } from '../../shared/serialization';
import { grayscaleColor, grayscaleElement } from '../../shared/grayscale';

const HISTORY_LIMIT = 100;

type DocumentElement = CollageDocument['elements'][number];

type DocumentStore = {
  document: CollageDocument;
  past: CollageDocument[];
  future: CollageDocument[];
  transactionStart: CollageDocument | null;
  isDirty: boolean;
  replaceDocument: (document: CollageDocument, markAsSaved?: boolean) => void;
  markSaved: () => void;
  addElement: (element: DocumentElement) => void;
  addAssets: (assets: AssetRecord[]) => void;
  removeAsset: (id: string) => void;
  addFont: (font: FontRecord) => void;
  updateElement: (id: string, patch: Partial<Omit<PhotoElement, 'id' | 'type'>> | Partial<Omit<TextElement, 'id' | 'type'>>) => void;
  updateElements: (updates: Array<{ id: string; patch: Partial<Omit<PhotoElement, 'id' | 'type'>> | Partial<Omit<TextElement, 'id' | 'type'>> }>) => void;
  deleteElement: (id: string) => void;
  duplicateElement: (id: string) => string | null;
  reorderElement: (id: string, targetIndex: number) => void;
  setLayout: (layout: CollageDocument['layout']) => void;
  updateCanvas: (patch: Partial<CollageDocument['canvas']>) => void;
  beginTransaction: () => void;
  commitTransaction: () => void;
  cancelTransaction: () => void;
  undo: () => void;
  redo: () => void;
};

const initialDocument = createDocument();

function isSameDocument(left: CollageDocument, right: CollageDocument): boolean {
  return serializeDocument(left) === serializeDocument(right);
}

function savedStatus(document: CollageDocument, savedSnapshot: string): boolean {
  return serializeDocument(document) !== savedSnapshot;
}

export const useDocumentStore = create<DocumentStore>((set, get) => {
  let savedSnapshot = serializeDocument(initialDocument);

  const applyDocument = (candidate: CollageDocument): void => {
    const nextDocument = validateDocument(candidate);
    const current = get();
    if (isSameDocument(current.document, nextDocument)) return;

    if (current.transactionStart) {
      set({ document: nextDocument, isDirty: savedStatus(nextDocument, savedSnapshot) });
      return;
    }

    set({
      document: nextDocument,
      past: [...current.past, current.document].slice(-HISTORY_LIMIT),
      future: [],
      isDirty: savedStatus(nextDocument, savedSnapshot),
    });
  };

  return {
    document: initialDocument,
    past: [],
    future: [],
    transactionStart: null,
    isDirty: false,

    replaceDocument: (candidate, markAsSaved = true) => {
      const document = validateDocument(candidate);
      if (markAsSaved) savedSnapshot = serializeDocument(document);
      set({ document, past: [], future: [], transactionStart: null, isDirty: !markAsSaved });
    },

    markSaved: () => {
      savedSnapshot = serializeDocument(get().document);
      set({ isDirty: false });
    },

    addElement: (element) => {
      const current = get().document;
      applyDocument({ ...current, elements: [...current.elements, current.canvas.blackAndWhite ? grayscaleElement(element) : element] });
    },

    addAssets: (assets) => {
      if (assets.length === 0) return;
      const current = get().document;
      const nextAssets = { ...current.assets };
      for (const asset of assets) nextAssets[asset.id] = asset;
      applyDocument({ ...current, assets: nextAssets });
    },

    removeAsset: (id) => {
      const current = get().document;
      if (!current.assets[id]) return;
      const assets = { ...current.assets };
      delete assets[id];
      const elements = current.elements.map((element) => element.type === 'photo' && element.assetId === id
        ? { ...element, assetId: null }
        : element);
      applyDocument({ ...current, assets, elements, canvas: { ...current.canvas, backgroundAssetId: current.canvas.backgroundAssetId === id ? null : current.canvas.backgroundAssetId } });
    },

    addFont: (font) => {
      const current = get().document;
      applyDocument({ ...current, fonts: { ...current.fonts, [font.id]: font } });
    },

    updateElement: (id, patch) => {
      const current = get().document;
      const elements = current.elements.map((element) => {
        if (element.id !== id) return element;
        const updated = { ...element, ...patch, id: element.id, type: element.type } as DocumentElement;
        return current.canvas.blackAndWhite ? grayscaleElement(updated) : updated;
      });
      applyDocument({ ...current, elements });
    },

    updateElements: (updates) => {
      if (updates.length === 0) return;
      const current = get().document;
      const patches = new Map(updates.map(({ id, patch }) => [id, patch]));
      const elements = current.elements.map((element) => {
        const patch = patches.get(element.id);
        if (!patch) return element;
        const updated = { ...element, ...patch, id: element.id, type: element.type } as DocumentElement;
        return current.canvas.blackAndWhite ? grayscaleElement(updated) : updated;
      });
      applyDocument({ ...current, elements });
    },

    deleteElement: (id) => {
      const current = get().document;
      applyDocument({ ...current, elements: current.elements.filter((element) => element.id !== id) });
    },

    duplicateElement: (id) => {
      const current = get().document;
      const source = current.elements.find((element) => element.id === id);
      if (!source) return null;

      const duplicateId = globalThis.crypto.randomUUID();
      const duplicate: DocumentElement = {
        ...source,
        id: duplicateId,
        name: `${source.name} copy`,
        transform: { ...source.transform, cx: source.transform.cx + 10, cy: source.transform.cy + 10 },
        ...(source.type === 'photo' ? { slotId: null } : {}),
      } as DocumentElement;
      applyDocument({ ...current, elements: [...current.elements, current.canvas.blackAndWhite ? grayscaleElement(duplicate) : duplicate] });
      return duplicateId;
    },

    reorderElement: (id, targetIndex) => {
      const current = get().document;
      const fromIndex = current.elements.findIndex((element) => element.id === id);
      if (fromIndex === -1 || !Number.isFinite(targetIndex)) return;
      const elements = [...current.elements];
      const [element] = elements.splice(fromIndex, 1);
      if (!element) return;
      const insertionIndex = Math.max(0, Math.min(elements.length, Math.trunc(targetIndex)));
      elements.splice(insertionIndex, 0, element);
      applyDocument({ ...current, elements });
    },

    setLayout: (layout) => applyDocument({ ...get().document, layout }),
    updateCanvas: (patch) => {
      const current = get().document;
      const canvas = { ...current.canvas, ...patch };
      const elements = patch.blackAndWhite === true ? current.elements.map(grayscaleElement) : current.elements;
      if (canvas.blackAndWhite) {
        canvas.background = canvas.background ? grayscaleColor(canvas.background) : null;
        canvas.backgroundImageSaturation = 0;
      }
      applyDocument({ ...current, canvas, elements });
    },

    beginTransaction: () => {
      if (!get().transactionStart) set({ transactionStart: get().document });
    },

    commitTransaction: () => {
      const current = get();
      const start = current.transactionStart;
      if (!start) return;
      if (isSameDocument(start, current.document)) {
        set({ transactionStart: null });
        return;
      }
      set({
        transactionStart: null,
        past: [...current.past, start].slice(-HISTORY_LIMIT),
        future: [],
      });
    },

    cancelTransaction: () => {
      const current = get();
      if (!current.transactionStart) return;
      const document = current.transactionStart;
      set({ document, transactionStart: null, isDirty: savedStatus(document, savedSnapshot) });
    },

    undo: () => {
      const current = get();
      if (current.transactionStart) {
        const document = current.transactionStart;
        set({ document, transactionStart: null, isDirty: savedStatus(document, savedSnapshot) });
        return;
      }
      const previous = current.past.at(-1);
      if (!previous) return;
      set({
        document: previous,
        past: current.past.slice(0, -1),
        future: [current.document, ...current.future].slice(0, HISTORY_LIMIT),
        isDirty: savedStatus(previous, savedSnapshot),
      });
    },

    redo: () => {
      const current = get();
      const next = current.future[0];
      if (!next) return;
      set({
        document: next,
        past: [...current.past, current.document].slice(-HISTORY_LIMIT),
        future: current.future.slice(1),
        isDirty: savedStatus(next, savedSnapshot),
      });
    },
  };
});
